import React, { useEffect, useState } from "react";
import { PencilSimple, Trash, Plus, X } from "@phosphor-icons/react";
import { api, fileUrl } from "../../lib/api";
import ImageUploader from "../../components/admin/ImageUploader";
import MultiImageUploader from "../../components/admin/MultiImageUploader";
import VideoUrlsInput from "../../components/admin/VideoUrlsInput";
import { toast } from "sonner";

const EMPTY = { title: "", excerpt: "", body: "", cover_image: "", images: [], video_urls: [], published: true };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/blog?include_drafts=true").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === "new") await api.post("/blog", form);
      else await api.put(`/blog/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/blog/${id}`);
    load();
  };

  return (
    <div className="p-8 md:p-10" data-testid="admin-blog">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Content</div>
          <h1 className="font-display text-5xl uppercase text-white leading-none">Journal</h1>
          <p className="text-white/60 mt-2 text-sm">Weekly project notes and updates.</p>
        </div>
        <button onClick={() => { setEditing("new"); setForm(EMPTY); }} className="btn-primary" data-testid="new-post-btn"><Plus size={18} weight="bold" /> New Post</button>
      </div>

      {posts.length === 0 ? (
        <div className="border-2 border-dashed border-[#333] p-16 text-center text-white/50">No posts yet.</div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="admin-card flex flex-col md:flex-row gap-5 items-start" data-testid={`post-row-${p.slug}`}>
              <div className="w-full md:w-40 aspect-[4/3] bg-atc-black overflow-hidden shrink-0">
                {p.cover_image ? (<img src={fileUrl(p.cover_image)} alt="" className="w-full h-full object-cover" />) : (<div className="w-full h-full flex items-center justify-center text-white/30 text-xs font-mono uppercase">No cover</div>)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {!p.published && <span className="bg-yellow-500 text-atc-black text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">Draft</span>}
                  <h3 className="font-heading uppercase text-white text-lg">{p.title}</h3>
                </div>
                <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mt-1">/blog/{p.slug} · {new Date(p.created_at).toLocaleDateString()}</div>
                <p className="text-white/60 text-sm mt-2 line-clamp-2">{p.excerpt || p.body.slice(0, 180)}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => { setEditing(p.id); setForm({ ...EMPTY, ...p }); }} className="border border-[#333] hover:border-atc-red p-2" data-testid={`edit-post-${p.slug}`}><PencilSimple size={14} weight="bold" /></button>
                <button onClick={() => del(p.id)} className="border border-[#333] hover:border-atc-red p-2" data-testid={`delete-post-${p.slug}`}><Trash size={14} weight="bold" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-atc-black/80 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-[#1f1f1f] border-2 border-atc-red max-w-3xl w-full p-8 my-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl uppercase text-white">{editing === "new" ? "New post" : "Edit post"}</h2>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-atc-red"><X size={24} weight="bold" /></button>
            </div>
            <div className="space-y-5">
              <div>
                <div className="admin-label">Title</div>
                <input className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" />
              </div>
              <div>
                <div className="admin-label">Excerpt</div>
                <input className="admin-input" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="post-excerpt" />
              </div>
              <ImageUploader value={form.cover_image} onChange={(p) => setForm({ ...form, cover_image: p })} folder="blog" label="Cover image" />
              <MultiImageUploader
                value={form.images || []}
                onChange={(arr) => setForm({ ...form, images: arr })}
                folder="blog"
                label="Additional photos (shown in the post body)"
              />
              <VideoUrlsInput
                value={form.video_urls || []}
                onChange={(arr) => setForm({ ...form, video_urls: arr })}
                label="Videos (YouTube / Vimeo / Facebook / Instagram / TikTok URLs)"
              />
              <div>
                <div className="admin-label">Body</div>
                <textarea rows={12} className="admin-input font-mono text-sm" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Write your post…" data-testid="post-body" />
              </div>
              <label className="flex items-center gap-2 text-white/80 text-sm font-heading uppercase tracking-widest">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="post-published" />
                Published (visible on public site)
              </label>
              <div className="flex gap-3 pt-4 border-t border-[#2a2a2a]">
                <button onClick={save} className="btn-primary" data-testid="save-post-btn">Save</button>
                <button onClick={() => setEditing(null)} className="btn-ghost-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
